import type { AxiosRequestConfig } from "axios"

import { request } from "../../../services/api"
import { announcesApprovalChange } from "../../../redux/notificationCounts/approvals-changed.ts"
import type { BaseApiResponse } from "../../../types/api.ts"
import { createFormData } from "../../../utils/form-data/create-form-data"
import { createFormDataNested } from "../../../utils/form-data/create-form-data-nested"
import type { LeadProposal } from "../../Lead/types/lead.documents.types.ts"
import type { ProposalFormData } from "../ProposalAddPage/components/proposal-form-data.tsx"
import type { ProposalRequestData } from "../redux/proposals.thunks.types.ts"
import type {
  ProposalFilterOptions,
  ProposalFilters
} from "../redux/proposals.types.ts"
import type { Proposal, ProposalListItem } from "../types/types.ts"
import type {
  CancelProposalData,
  LeadId,
  ProposalId,
  RejectProposalData,
  SubmitForReviewData,
  UploadCompanyLogoData,
  UploadProposalData
} from "./services.types.ts"
import { afterStatusWrite } from "../../StatusAutomation/helpers/automatic-move-events.ts"

const BASE_URL = "/proposal"

const PATHS = {
  // Proposals
  getProposals: `${BASE_URL}/`,
  getPendingProposals: `${BASE_URL}/pending-review/`,
  submitForReview: (id: ProposalId) => `${BASE_URL}/${id}/submit-for-review/`,
  getProposalInitialValues: (leadID: LeadId) =>
    `${BASE_URL}/initial/?lead_id=${leadID}`,
  createProposal: `${BASE_URL}/`,
  previewProposal: `${BASE_URL}/preview/`,
  generateProposal: `${BASE_URL}/generate/`,
  uploadCompanyLogo: `${BASE_URL}/upload-logo/`,
  uploadFile: `${BASE_URL}/create-with-file/`,
  filterOptions: `${BASE_URL}/filter_options/`,
  proposal: (id: ProposalId) => ({
    get: `${BASE_URL}/${id}/`,
    update: `${BASE_URL}/${id}/`,
    save: `${BASE_URL}/${id}/save-file/`,
    reject: `${BASE_URL}/${id}/decline/`,
    cancel: `${BASE_URL}/${id}/cancel/`,
    approve: `${BASE_URL}/${id}/approve/`,
    duplicate: `${BASE_URL}/${id}/create-new-version/`,
    sentToClient: `${BASE_URL}/${id}/send/`,
    sendEmail: `${BASE_URL}/${id}/send-email/`
  })
}

export const getProposals = (params?: ProposalFilters) =>
  request.api.get<ProposalListItem[], BaseApiResponse<unknown>>(
    PATHS.getProposals,
    params
  )

export const getPendingProposals = (team?: string | null) =>
  request.api.get<ProposalListItem[], BaseApiResponse<unknown>>(
    PATHS.getPendingProposals,
    team ? { team } : undefined
  )

export const getProposalInitialValues = (leadID: LeadId) =>
  request.api.get<ProposalFormData, BaseApiResponse<unknown>>(
    PATHS.getProposalInitialValues(leadID)
  )

export const getProposalById = (id: ProposalId) =>
  request.api.get<Proposal, BaseApiResponse<unknown>>(PATHS.proposal(id).get)

export const createProposal = (data: ProposalRequestData) =>
  request.api.post<BaseApiResponse<Proposal>, BaseApiResponse<unknown>>(
    PATHS.createProposal,
    data
  )

export const previewProposal = (data: ProposalRequestData) =>
  request.api.post<string, BaseApiResponse<unknown>>(
    PATHS.previewProposal,
    data
  )

export const generateProposal = (data: ProposalRequestData) =>
  request.api.post<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.generateProposal,
    data
  )

export const saveProposalFile = (id: ProposalId) =>
  request.api.post<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.proposal(id).save
  )

export const updateProposal = (id: ProposalId, data: ProposalRequestData) =>
  request.api.patch<BaseApiResponse<Proposal>, BaseApiResponse<unknown>>(
    PATHS.proposal(id).update,
    data
  )

export const submitForReview = ({
  proposalId,
  attachmentId: attachment_id
}: SubmitForReviewData) => {
  return announcesApprovalChange(
    request.api.post<BaseApiResponse<LeadProposal>, BaseApiResponse<unknown>>(
      PATHS.submitForReview(proposalId),
      { attachment_id }
    )
  )
}

export const rejectProposal = ({ id, reason }: RejectProposalData) => {
  return announcesApprovalChange(
    request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
      PATHS.proposal(id).reject,
      { reason }
    )
  )
}

export const cancelProposal = ({ id, reason, notes }: CancelProposalData) => {
  return announcesApprovalChange(
    request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
      PATHS.proposal(id).cancel,
      { reason, notes }
    )
  )
}

export const approveProposal = (proposalId: ProposalId) => {
  return announcesApprovalChange(
    request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
      PATHS.proposal(proposalId).approve
    )
  )
}

export const uploadCompanyLogo = (logo: UploadCompanyLogoData) => {
  const headers = { "Content-Type": "multipart/form-data" }
  const formData = createFormDataNested(logo, "file")

  return request.api.post<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.uploadCompanyLogo,
    formData,
    { headers }
  )
}

export const duplicateProposal = (proposalId: ProposalId) => {
  return request.api.post<
    BaseApiResponse<LeadProposal>,
    BaseApiResponse<unknown>
  >(PATHS.proposal(proposalId).duplicate)
}

export const uploadProposal = (
  data: UploadProposalData,
  options: AxiosRequestConfig = {}
) => {
  const headers = { "Content-Type": "multipart/form-data" }
  const formData = createFormData(data, "file")

  return request.api.post<
    BaseApiResponse<LeadProposal>,
    BaseApiResponse<unknown>
  >(PATHS.uploadFile, formData, { headers, ...options })
}

const sentProposalToClientRequest = (proposalId: ProposalId) => {
  return request.api.post<
    BaseApiResponse<LeadProposal>,
    BaseApiResponse<unknown>
  >(PATHS.proposal(proposalId).sentToClient)
}

const sendProposalEmailRequest = (proposalId: ProposalId) => {
  return request.api.post<
    BaseApiResponse<LeadProposal>,
    BaseApiResponse<unknown>
  >(PATHS.proposal(proposalId).sendEmail)
}

export const getFilterOptions = () => {
  return request.api.get<ProposalFilterOptions, BaseApiResponse<unknown>>(
    PATHS.filterOptions
  )
}

export const sentProposalToClient = (proposalId: ProposalId) =>
  afterStatusWrite(sentProposalToClientRequest(proposalId))

export const sendProposalEmail = (proposalId: ProposalId) =>
  afterStatusWrite(sendProposalEmailRequest(proposalId))
